
(() => {
  "use strict";

  // Elite Membership controller.
  // Only manages the membership product; never changes regular product prices.

  const CONFIG = {
    membershipProductId: "8997234671837",

    // IMPORTANT: Replace "membership" with the actual product handle
    // if the storefront URL is different.
    membershipProductUrl: "/products/membership.js",

    cartUrl: "/cart.js",
    addUrl: "/cart/add.js",
    changeUrl: "/cart/change.js",

    syncHeader: "X-Elite-Membership-Sync"
  };

  let membershipVariantId = null;
  let syncInProgress = false;
  let syncScheduled = false;
  let lastSyncTime = 0;
  let drawerObserver = null;
  let manualRemovalSuppressed = false;

  const log = (...args) =>
    console.info("[Elite Membership Cart]", ...args);

  // --------------------------------------------------
  // 1. Resolve the membership PRODUCT to its VARIANT ID
  // --------------------------------------------------
  async function getMembershipVariantId() {
    if (membershipVariantId) return membershipVariantId;

    const response = await fetch(CONFIG.membershipProductUrl, {
      credentials: "same-origin",
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error(
        "Cannot load membership product. Check membershipProductUrl."
      );
    }

    const product = await response.json();

    if (String(product.id) !== CONFIG.membershipProductId) {
      throw new Error(
        `Product ID mismatch. Expected ${CONFIG.membershipProductId}, ` +
        `received ${product.id}. Check the product URL.`
      );
    }

    const variant = product.variants?.find(item => item.available);

    if (!variant) {
      throw new Error("No available membership variant was found.");
    }

    membershipVariantId = String(variant.id);

    log("Membership variant identified:", membershipVariantId);

    return membershipVariantId;
  }

  // --------------------------------------------------
  // 2. Read the actual Shopify cart
  // --------------------------------------------------

  async function getCart() {
    const response = await fetch(CONFIG.cartUrl, {
      credentials: "same-origin",
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error("Could not read Shopify cart.");
    }

    return response.json();
  }

  // --------------------------------------------------
  // 3. Preserve the legacy customer eligibility rule
  // --------------------------------------------------

  function getCustomerEligibility() {
    try {
      const cookie = document.cookie
        .split("; ")
        .find(item => item.startsWith("shopify_user_data="));

      if (!cookie) return false;

      const raw = decodeURIComponent(
        cookie.substring(cookie.indexOf("=") + 1)
      );

      const customer = JSON.parse(raw);

      const loggedIn = Boolean(customer?.id);

      // Verify this property against the actual legacy cookie structure.
      const isElite =
        customer?.eliteMember === true ||
        customer?.eliteMember === "true";

      return loggedIn && !isElite;
    } catch (error) {
      console.warn(
        "[Elite Membership Cart] Could not verify customer eligibility."
      );
      return false;
    }
  }

  // --------------------------------------------------
  // 4. Standard Shopify Ajax Cart API requests
  // --------------------------------------------------

  async function cartMutation(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        [CONFIG.syncHeader]: "1"
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(
        `Cart update failed (${response.status}): ${await response.text()}`
      );
    }

    return response.json();
  }

  async function addMembership() {
    const id = await getMembershipVariantId();

    await cartMutation(CONFIG.addUrl, {
      items: [{ id: Number(id), quantity: 1 }]
    });

    log("Added one membership item.");
  }

  async function setMembershipQuantity(lineKey, quantity) {
    await cartMutation(CONFIG.changeUrl, {
      id: lineKey,
      quantity
    });
  }

  // --------------------------------------------------
  // 5. Membership-only reconciliation
  // --------------------------------------------------

  async function reconcileCart() {
    if (syncInProgress) return;

    syncInProgress = true;

    try {
      const memberId = await getMembershipVariantId();
      const cart = await getCart();

      const isMembership = item =>
        String(item.variant_id) === memberId;

      const regularItems = cart.items.filter(item => !isMembership(item));
      const membershipItems = cart.items.filter(isMembership);

      // Rule A: No regular products remain.
      // Remove every membership line.
      if (regularItems.length === 0) {
        for (const item of membershipItems) {
          await setMembershipQuantity(item.key, 0);
        }

        if (membershipItems.length > 0) {
          log("Removed membership: cart has no regular products.");
          notifyCartChanged();
        }

        return;
      }

      // Rule B: Membership already exists.
      // Keep one membership line with quantity 1.
      if (membershipItems.length > 0) {
        const [keep, ...duplicates] = membershipItems;
        let changed = false;

        if (Number(keep.quantity) !== 1) {
          await setMembershipQuantity(keep.key, 1);
          changed = true;
          log("Corrected membership quantity to 1.");
        }

        for (const item of duplicates) {
          await setMembershipQuantity(item.key, 0);
          changed = true;
          log("Removed duplicate membership line.");
        }

        if (changed) notifyCartChanged();

        return;
      }

      // Rule C: Regular products exist; membership is absent.
      // Only add it for eligible customers.
        if (!manualRemovalSuppressed) {
        await addMembership();
        notifyCartChanged();
        } else {
        log("Membership was manually removed; respecting customer choice.");
        }

    } catch (error) {
      console.error("[Elite Membership Cart] Sync failed:", error);
    } finally {
      syncInProgress = false;
    }
  }

  // --------------------------------------------------
  // 6. Ask the theme to refresh its cart UI
  // --------------------------------------------------

  function notifyCartChanged() {
    // This is a notification, not a guaranteed native theme refresh.
    document.dispatchEvent(
      new CustomEvent("elite-membership:cart-changed")
    );

    document.dispatchEvent(
      new CustomEvent("cart:refresh")
    );
  }

  // --------------------------------------------------
  // 7. Schedule checks without overlapping them
  // --------------------------------------------------

  function scheduleSync() {
    if (syncScheduled) return;

    syncScheduled = true;

    setTimeout(async () => {
      syncScheduled = false;

      const now = Date.now();

      if (now - lastSyncTime < 300) {
        scheduleSync();
        return;
      }

      lastSyncTime = now;
      await reconcileCart();
    }, 350);
  }

  // --------------------------------------------------
  // 8. Monitor standard fetch-based cart mutations
  // --------------------------------------------------

    function installFetchMonitor() {
    if (window.__eliteMembershipFetchMonitorInstalled) return;

    window.__eliteMembershipFetchMonitorInstalled = true;

    const originalFetch = window.fetch;

    window.fetch = function(input, init) {
        const self = this;
        const args = arguments;

        const rawUrl =
        typeof input === "string" ? input : input?.url || "";

        let url;

        try {
        url = new URL(rawUrl, window.location.origin);
        } catch {
        return originalFetch.apply(self, args);
        }

        const method = (
        init?.method ||
        (typeof Request !== "undefined" && input instanceof Request
            ? input.method
            : "GET")
        ).toUpperCase();

        const isCartMutation =
        url.origin === window.location.origin &&
        /^\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname) &&
        method === "POST";

        const headers = new Headers(
        init?.headers ||
        (typeof Request !== "undefined" && input instanceof Request
            ? input.headers
            : undefined)
        );

        const isOurRequest =
        headers.get(CONFIG.syncHeader) === "1";

        if (!isCartMutation || isOurRequest) {
        return originalFetch.apply(self, args);
        }

        let payload = null;

        try {
        if (typeof init?.body === "string") {
            payload = JSON.parse(init.body);
        }
        } catch {
        // If the theme uses a different payload format,
        // normal cart requests will still work.
        }

        const isAddRequest = /\/cart\/add(?:\.js)?$/.test(url.pathname);

        // For a potential line removal, read the cart before
        // sending the removal so we can identify the target line.
        const isPotentialRemoval =
        /\/cart\/change(?:\.js)?$/.test(url.pathname) &&
        Number(payload?.quantity) === 0 &&
        payload?.id != null;

        if (isPotentialRemoval) {
        return (async () => {
            try {
            const [cart, memberId] = await Promise.all([
                getCart(),
                getMembershipVariantId()
            ]);

            const targetId = String(payload.id);

            const targetIsMembership = cart.items.some(item =>
                String(item.variant_id) === memberId &&
                (
                String(item.key) === targetId ||
                String(item.variant_id) === targetId
                )
            );

            if (targetIsMembership) {
                manualRemovalSuppressed = true;
                log("Manual membership removal detected.");
            }
            } catch (error) {
            console.warn(
                "[Elite Membership Cart] Could not identify removed line.",
                error
            );
            }

            const response = await originalFetch.apply(self, args);

            if (response.ok) {
            scheduleSync();
            }

            return response;
        })();
        }

        const result = originalFetch.apply(self, args);

        result.then(response => {
        if (!response.ok) return;

        // A new regular-product add permits automatic membership again.
        if (isAddRequest) {
            manualRemovalSuppressed = false;
        }

        scheduleSync();
        }).catch(() => {
        // Do not interfere with the theme's cart handling.
        });

        return result;
    };
    }

    // --------------------------------------------------
  // 9. Detect cart drawer opening
  // --------------------------------------------------

  function isCartDrawerOpen() {
    return Boolean(document.querySelector([
      "cart-drawer[open]",
      "cart-drawer.active",
      "#CartDrawer.active",
      "#CartDrawer.is-open",
      ".cart-drawer.active",
      ".cart-drawer.is-open"
    ].join(",")));
  }

  function installDrawerObserver() {
    if (drawerObserver || !document.body) return;

    drawerObserver = new MutationObserver(() => {
      if (isCartDrawerOpen()) scheduleSync();
    });

    drawerObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["open", "class", "aria-hidden"]
    });
  }

  // --------------------------------------------------
  // 10. Initialize
  // --------------------------------------------------

  function init() {
    installFetchMonitor();
    installDrawerObserver();

    [
      "cart:updated",
      "cart:refresh",
      "cart:add",
      "product:add",
      "product:added",
      "ajaxProduct:added"
    ].forEach(eventName => {
      document.addEventListener(eventName, scheduleSync);
    });

    // Covers direct visits to the main cart page.
    if (window.location.pathname.replace(/\/$/, "") === "/cart") {
      scheduleSync();
    }

    // Covers an already-open drawer.
    if (isCartDrawerOpen()) scheduleSync();

    log("Controller initialized.");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
